/**
 * Candidate show tab layout — edit **`buildCandidateShowTabSections`** to reorder tabs or change grouping.
 *
 * - **`layout: 'tabs'`** — rendered as a normal MUI tab row (segment order = array order here).
 * - **`layout: 'dropdown'`** — one menu button (`menuLabel`); `tabs` set menu order and URL paths.
 *
 * For `<TabbedShowLayout>`, always use **`prepareCandidateShowTabLayout`** so `sections` and children
 * stay in sync.
 */
import type { ReactElement } from 'react';
import {
  AddressesTab,
  CMActionsTab,
  ChecksTab,
  CriminalRecordsTab,
  DrugTestTab,
  FederalRecordsTab,
  IDVTab,
  MVRTab,
  MvrAccidentTab,
  MvrActionTab,
  MvrLicenseTab,
  MvrViolationTab,
  NationalRecordsTab,
  NotesTab,
  OneOffChecksTab,
  RunMvrTab,
  SexOffenderRecordsTab,
  SummaryTab,
  WatchlistRecordsTab,
} from '@/pages/Candidate/view/tabs';
import type { CandidateTab, UseCandidateTabs } from '@/pages/Candidate/hooks/useCandidateTabs/types';

function compactTabs(nodes: Array<ReactElement | false | null | undefined>): ReactElement[] {
  return nodes.filter((n): n is ReactElement => Boolean(n));
}

/** One strip segment: MUI `Tabs` row, or a dropdown button whose menu mirrors `tabs` order. */
export type CandidateShowTabLayoutSection =
  | { layout: 'tabs'; tabs: ReactElement[] }
  | {
      layout: 'dropdown';
      menuLabel: string;
      /** Menu row labels by tab `path` (wrappers do not expose inner `<Tab label />`). */
      menuItemLabels: Record<string, string>;
      tabs: ReactElement[];
    };

/** Builds the three strip segments (leading tabs → Records menu → trailing tabs). */
function buildCandidateShowTabSections(
  shouldRenderTab: UseCandidateTabs['shouldRenderTab'],
  path: (p: CandidateTab) => string
): CandidateShowTabLayoutSection[] {
  return [
    {
      layout: 'tabs',
      tabs: compactTabs([
        shouldRenderTab.summary && <SummaryTab key="summary" />,
        shouldRenderTab.addresses && <AddressesTab key="addresses" path={path('addresses')} />,
        shouldRenderTab.checks && <ChecksTab key="checks" path={path('checks')} />,
        shouldRenderTab.one_off_checks && <OneOffChecksTab key="one_off_checks" path={path('one_off_checks')} />,
        shouldRenderTab.cm_actions && <CMActionsTab key="cm_actions" path={path('cm_actions')} />,
        shouldRenderTab.idv && <IDVTab key="idv" path={path('idv')} />,
        shouldRenderTab.drug_test && <DrugTestTab key="drug_test" path={path('drug_test')} />,
        shouldRenderTab.notes && <NotesTab key="notes" path={path('notes')} />,
      ]),
    },
    {
      layout: 'dropdown',
      menuLabel: 'Records',
      menuItemLabels: {
        criminal_records: 'County',
        sexoff_records: 'SexOffender',
        national_records: 'National',
        watchlist_records: 'Watchlist',
        federal_records: 'Federal',
        mvr: 'MVR',
      },
      tabs: compactTabs([
        shouldRenderTab.criminal_records && (
          <CriminalRecordsTab key="criminal_records" path={path('criminal_records')} />
        ),
        shouldRenderTab.sexoff_records && <SexOffenderRecordsTab key="sexoff_records" path={path('sexoff_records')} />,
        shouldRenderTab.national_records && (
          <NationalRecordsTab key="national_records" path={path('national_records')} />
        ),
        shouldRenderTab.watchlist_records && (
          <WatchlistRecordsTab key="watchlist_records" path={path('watchlist_records')} />
        ),
        shouldRenderTab.federal_records && <FederalRecordsTab key="federal_records" path={path('federal_records')} />,
        shouldRenderTab.mvr && <MVRTab key="mvr" path={path('mvr')} />,
      ]),
    },
    {
      layout: 'tabs',
      tabs: compactTabs([
        shouldRenderTab.run_mvr && <RunMvrTab key="run_mvr" path={path('run_mvr')} />,
        shouldRenderTab.mvr_actions && <MvrActionTab key="mvr_actions" path={path('mvr_actions')} />,
        shouldRenderTab.mvr_accidents && <MvrAccidentTab key="mvr_accidents" path={path('mvr_accidents')} />,
        shouldRenderTab.mvr_violations && <MvrViolationTab key="mvr_violations" path={path('mvr_violations')} />,
        shouldRenderTab.mvr_license && <MvrLicenseTab key="mvr_license" path={path('mvr_license')} />,
      ]),
    },
  ];
}

function flattenCandidateShowTabSections(sections: CandidateShowTabLayoutSection[]): ReactElement[] {
  return sections.flatMap((s) => s.tabs);
}

/** Prefer this in views: one call returns both `sections` (for custom `tabs` UI) and `tabElements` (layout children). */
export function prepareCandidateShowTabLayout(
  shouldRenderTab: UseCandidateTabs['shouldRenderTab'],
  pathFn: (p: CandidateTab) => string
): { sections: CandidateShowTabLayoutSection[]; tabElements: ReactElement[] } {
  const sections = buildCandidateShowTabSections(shouldRenderTab, pathFn);
  return { sections, tabElements: flattenCandidateShowTabSections(sections) };
}
